/**
 * Marcadores de carga con la forma del contenido que va a llegar.
 *
 * # Por qué existe como componente y no como receta
 *
 * El patrón estaba en Standards.md §21 **como código para copiar y pegar**, y el
 * resultado predecible: diecinueve ficheros con su propio `animate-pulse`, cada
 * uno con su gris, su altura y su redondeo. Un marcador de carga es de las cosas
 * que más se ven —está en pantalla justo cuando alguien llega— así que que cada
 * página tenga el suyo se nota.
 *
 * # Lo que arregla, que no es sólo estética
 *
 * ⚠️ **Una sección que se pinta vacía mientras carga no está «vacía»: está
 * AFIRMANDO que no hay nada.** No es lo mismo, y la diferencia se ve en la
 * integración de Bambu: su estado arrancaba en «no conectada», así que durante
 * un instante decía «conéctate» sobre una cuenta que ya estaba conectada — y se
 * podía llegar a pulsar el botón. Un `null` mientras carga es la versión muda
 * del mismo error.
 *
 * La regla, y es la que hay que respetar al usar esto: **tres estados, no dos**.
 * Cargando, vacío y con contenido. El vacío sólo se puede afirmar después de
 * haber preguntado.
 *
 * # Detalles que no son adorno
 *
 * * **`role="status"` con un texto oculto.** Sin él, quien navega con lector de
 *   pantalla se encuentra una región que cambia sin que nada le diga que estaba
 *   cargando. Las barras van `aria-hidden`: son decoración.
 * * **`motion-safe:`**. Un pulso perpetuo es exactamente lo que
 *   `prefers-reduced-motion` existe para apagar, y hay gente a la que le produce
 *   náusea. Con el prefijo, quien lo pidió ve un bloque quieto — que sigue
 *   comunicando lo mismo.
 */
import React from 'react';

/** El gris. Un solo sitio, para que no haya cinco tonos por la aplicación. */
const BASE =
    'motion-safe:animate-pulse rounded bg-slate-200/70 dark:bg-slate-700/50';

export interface SkeletonProps {
    /** Tamaño y forma. Se espera al menos una altura: `h-3 w-40`. */
    className?: string;
}

/**
 * Una barra. El primitivo: el tamaño lo pone quien la usa.
 *
 * Alturas de la casa, para que dos pantallas no discrepen: `h-3` para texto
 * principal, `h-2.5` para secundario, `h-5` para una insignia, `h-8` para una
 * cifra grande.
 */
export function Skeleton({ className = 'h-3 w-full' }: SkeletonProps) {
    return <div className={`${BASE} ${className}`} aria-hidden />;
}

export interface SkeletonRowsProps {
    /** Cuántas filas. Que se parezca a lo que suele haber, no al máximo. */
    rows?: number;
    /** Un círculo a la izquierda, para listas con miniatura o avatar. */
    avatar?: boolean;
    /** Texto para el lector de pantalla. */
    label?: string;
    className?: string;
}

/** Las filas, sin región de estado.
 *
 *  ⚠️ Interno a propósito. `role="status"` va UNA vez por bloque que carga: si
 *  `SkeletonBlock` reutilizara el componente público metería una región dentro
 *  de otra y el lector de pantalla anunciaría «Cargando» dos veces por la misma
 *  espera. Anidar regiones vivas es de los errores de accesibilidad que no se
 *  ven mirando la pantalla. */
function Filas({ rows, avatar }: { rows: number; avatar: boolean }) {
    return (
        <>
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                    {avatar && <Skeleton className="h-8 w-8 shrink-0 rounded-full" />}
                    <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                        <Skeleton className="h-3 w-2/5" />
                        <Skeleton className="h-2.5 w-1/4" />
                    </div>
                    <Skeleton className="h-5 w-16 rounded-full" />
                </div>
            ))}
        </>
    );
}

/**
 * Una lista: filas de dos líneas, la de arriba ancha y la de abajo corta.
 *
 * Es la forma que sirve para casi todo —pedidos, clientes, impresoras,
 * agentes— y la que hay que preferir antes de componer una a mano.
 */
export function SkeletonRows({
    rows = 4,
    avatar = false,
    label = 'Cargando',
    className = '',
}: SkeletonRowsProps) {
    return (
        <div className={`flex flex-col gap-3 ${className}`} role="status" aria-live="polite">
            <span className="sr-only">{label}</span>
            <Filas rows={rows} avatar={avatar} />
        </div>
    );
}

export interface SkeletonBlockProps {
    /** Filas dentro de la tarjeta. */
    rows?: number;
    /** Una barra de título ENCIMA de la tarjeta, como en Configuración. */
    heading?: boolean;
    label?: string;
    className?: string;
}

/**
 * Una tarjeta con filas dentro: la unidad de una sección entera.
 *
 * Trae su propio borde y su fondo porque lo que sustituye también los tiene —
 * un bloque gris flotando donde iba una tarjeta produce el salto de maquetación
 * que esto existe para evitar.
 */
export function SkeletonBlock({
    rows = 4,
    heading = false,
    label = 'Cargando',
    className = '',
}: SkeletonBlockProps) {
    return (
        <div className={`flex flex-col gap-2 ${className}`} role="status" aria-live="polite">
            <span className="sr-only">{label}</span>
            {heading && <Skeleton className="h-3 w-40" />}
            <div className="rounded-xl border border-border-subtle dark:border-border-subtle-dark bg-white dark:bg-surface-dark p-4 flex flex-col gap-3">
                <Filas rows={rows} avatar={false} />
            </div>
        </div>
    );
}

export default Skeleton;
